import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { getPlatformStreamingPort } from './container';
import type {
  PlatformStreamSession,
  PlatformStreamingState,
  StreamingPlatform,
} from './ports/PlatformStreamingPort';

export interface PlatformSettings {
  rtmpUrl: string;
  streamKey: string;
  enabled: boolean;
}

interface PlatformStreamStore {
  state: PlatformStreamingState;
  destinations: Record<StreamingPlatform, PlatformSettings>;
  session: PlatformStreamSession | null;
  error: string | null;
  updateDestination(platform: StreamingPlatform, patch: Partial<Omit<PlatformSettings, 'enabled'>>): void;
  togglePlatform(platform: StreamingPlatform, enabled?: boolean): void;
  saveSettingsToServer(tokenServerUrl: string, username: string): Promise<void>;
  start(tokenServerUrl: string, roomName: string): Promise<void>;
  stop(tokenServerUrl: string): Promise<void>;
}

const DEFAULT_DESTINATIONS: Record<StreamingPlatform, PlatformSettings> = {
  youtube: {
    rtmpUrl: 'rtmp://a.rtmp.youtube.com/live2',
    streamKey: '',
    enabled: false,
  },
  twitch: {
    rtmpUrl: 'rtmp://live.twitch.tv/app',
    streamKey: '',
    enabled: false,
  },
  kick: {
    rtmpUrl: 'rtmps://fa723fc1b171.global-contribute.live-video.net/app',
    streamKey: '',
    enabled: false,
  },
  custom: {
    rtmpUrl: '',
    streamKey: '',
    enabled: false,
  },
};

export const usePlatformStreamStore = create<PlatformStreamStore>()(
  persist(
    (set, get) => ({
      state: 'idle',
      destinations: DEFAULT_DESTINATIONS,
      session: null,
      error: null,

      updateDestination: (platform, patch) =>
        set((current) => ({
          destinations: {
            ...current.destinations,
            [platform]: {
              ...current.destinations[platform],
              ...patch,
            },
          },
        })),

      togglePlatform: (platform, enabled) =>
        set((current) => ({
          destinations: {
            ...current.destinations,
            [platform]: {
              ...current.destinations[platform],
              enabled: enabled ?? !current.destinations[platform].enabled,
            },
          },
        })),

      saveSettingsToServer: async (tokenServerUrl, username) => {
        if (!username) {
          throw new Error('Cannot save stream settings: user is not identified');
        }

        const { destinations } = get();
        const enabledPlatforms = (Object.keys(destinations) as StreamingPlatform[]).filter(
          (platform) => destinations[platform].enabled
        );

        const settingsPayload = enabledPlatforms.reduce((acc, platform) => {
          const dest = destinations[platform];
          // Send url and key separately — the server assembles the final RTMP URL
          // so it can apply platform-specific path rules correctly.
          acc[platform] = {
            url: dest.rtmpUrl,
            key: dest.streamKey,
          };
          return acc;
        }, {} as Record<StreamingPlatform, { url: string; key: string }>);

        const response = await fetch(`${tokenServerUrl.replace(/\/$/, '')}/user/stream/settings/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            settings: settingsPayload,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to save settings: ${await response.text()}`);
        }
      },

      start: async (tokenServerUrl, roomName) => {
        const destinations = get().destinations;
        const enabledPlatforms = (Object.keys(destinations) as StreamingPlatform[]).filter(
          (p) => destinations[p].enabled
        );

        if (enabledPlatforms.length === 0) {
          set({ state: 'error', error: 'Select at least one platform before starting the stream' });
          throw new Error('No platforms enabled');
        }

        const missingConfig = enabledPlatforms.filter(
          (p) => !destinations[p].rtmpUrl.trim() || !destinations[p].streamKey.trim()
        );
        if (missingConfig.length > 0) {
          const names = missingConfig.join(', ');
          set({ state: 'error', error: `Missing RTMP URL or stream key for: ${names}` });
          throw new Error(`Missing config for platforms: ${names}`);
        }

        set({ state: 'starting', error: null });
        try {
          const session = await getPlatformStreamingPort().start({
            tokenServerUrl,
            roomName,
            platforms: enabledPlatforms.map((name) => ({ name })),
          });
          set({ state: 'live', session });
        } catch (error) {
          set({ state: 'error', error: error instanceof Error ? error.message : 'Failed to start platform stream' });
          throw error;
        }
      },

      stop: async (tokenServerUrl) => {
        const { session } = get();

        if (!session) {
          set({ state: 'idle' });
          return;
        }

        set({ state: 'stopping', error: null });

        try {
          await getPlatformStreamingPort().stop(tokenServerUrl, session.egressId);
          set({ state: 'idle', session: null });
        } catch (error) {
          set({
            state: 'error',
            error: error instanceof Error ? error.message : 'Failed to stop platform stream',
          });
          throw error;
        }
      },
    }),
    {
      name: 'synth-cohost-platform-stream',
      partialize: (state) => ({
        destinations: (Object.keys(state.destinations) as StreamingPlatform[]).reduce((acc, platform) => {
          acc[platform] = {
            ...state.destinations[platform],
            streamKey: '',
          };
          return acc;
        }, {} as Record<StreamingPlatform, PlatformSettings>),
      }),
      merge: (persistedState: any, currentState) => {
        const destinations = { ...currentState.destinations };

        if (persistedState && typeof persistedState === 'object') {
          const persisted = persistedState.destinations ?? null;

          if (persisted) {
            // Merge each platform individually, falling back to defaults for empty URLs
            (Object.keys(destinations) as StreamingPlatform[]).forEach((platform) => {
              if (persisted[platform]) {
                const persistedRtmp = persisted[platform].rtmpUrl || '';
                const defaultRtmp = currentState.destinations[platform].rtmpUrl;

                // If persisted URL is empty, use the code default
                const rtmpUrl = persistedRtmp || defaultRtmp;

                destinations[platform] = {
                  ...destinations[platform],
                  ...persisted[platform],
                  rtmpUrl,
                  streamKey: '', // always clear from localStorage
                };
              }
            });
          } else if (persistedState.destination) {
            // Migrate from old single-destination format
            const oldDest = persistedState.destination;
            const platform = oldDest.platform as StreamingPlatform;
            if (platform && destinations[platform]) {
              destinations[platform] = {
                ...destinations[platform],
                rtmpUrl: oldDest.rtmpUrl || destinations[platform].rtmpUrl,
                enabled: true,
              };
            }
          }
        }

        return {
          ...currentState,
          destinations,
        };
      }
    },
  ),
);
